/**
 * Client-side codec for the OAuth `state` the server appends to a provider authorize URL
 * (`src/lib/rudderstack-accounts.ts` `buildOauthState`): base64url JSON `{ v: 1, o, n }`.
 * The opener only ever needs `n`, to match a broadcast against the flow it started.
 *
 * Declared locally on purpose: this is the WIRE shape carried through the provider and the webapp
 * trampoline (spec §4.2), not a router type — the server's `OauthState` is the same contract, and
 * `decodeOauthState` is the one place that must agree with `src/lib/rudderstack-accounts.ts`.
 */
export interface OauthState {
  v: 1;
  o: string;
  n: string;
}

function fromBase64Url(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return atob(padded);
}

function toBase64Url(input: string): string {
  return btoa(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeOauthState(state: string | null | undefined): OauthState | null {
  if (!state) return null;
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(state));
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      (parsed as { v?: unknown }).v === 1 &&
      typeof (parsed as { o?: unknown }).o === 'string' &&
      typeof (parsed as { n?: unknown }).n === 'string'
    ) {
      const { o, n } = parsed as { o: string; n: string };
      return { v: 1, o, n };
    }
    return null;
  } catch {
    return null;
  }
}

/** Test helper — the server mints real states; the UI only decodes. */
export function encodeOauthStateForTest(state: OauthState): string {
  return toBase64Url(JSON.stringify(state));
}
